import React, { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { blobErrorDetail, downloadBlob } from "../../lib/siteInfo";
import { LaunchCard, StatusBadge } from "./ui";

function RowTable({ title, rows, columns, testId }) {
  if (!rows.length) return null;
  return (
    <div className="mt-4">
      <div className="eyebrow mb-1">{title}</div>
      <div className="max-h-[320px] overflow-auto border" style={{ borderColor: "var(--border)" }}>
        <table className="w-full text-sm min-w-[560px]" data-testid={testId}>
          <tbody>
            {rows.map((r) => (
              <tr key={`${title}-${r.row}`} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`${testId}-row-${r.row}`}>
                <td className="px-2 py-1.5 text-xs" style={{ color: "var(--muted)" }}>Row {r.row}</td>
                {columns.map((c) => <td key={c} className="px-2 py-1.5">{String(r[c] ?? "")}</td>)}
                <td className="px-2 py-1.5">{r.errors.length ? <span className="text-xs" style={{ color: "#842029" }}>{r.errors.join("; ")}</span> : <StatusBadge status="pass" label="OK" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EmployerImport() {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const template = async () => {
    try { await downloadBlob(api.get("/admin/employer-import/template", { responseType: "blob" }), "employer-users-template.xlsx"); }
    catch (e) { toast.error(formatApiError(await blobErrorDetail(e))); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/employer-import/check", form);
      setCheck(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/employer-import/${check.batch_id}/commit`);
      toast.success(`${data.users} employer user(s) and ${data.employers} employer(s) added; ${data.queued} invitation(s) queued, ${data.sent} sent`);
      setCheck(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <LaunchCard eyebrow="Spreadsheet" title="Employer users from a spreadsheet" testId="employer-import-card">
      <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
        Columns: name, work email, employer, role, invoice approver (yes/no), plus a sheet for employers not in the portal yet. Every row is checked before anything is saved.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-ghost flex items-center gap-2" onClick={template} data-testid="employer-import-template-btn"><Download size={14} /> Download template</button>
        <label className="btn-ghost cursor-pointer" data-testid="employer-import-upload-label">
          {busy && !check ? "Checking…" : "Upload and check"}
          <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="employer-import-file" />
        </label>
      </div>
      {check && (
        <div data-testid="employer-import-check">
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
            <StatusBadge status={check.problems ? "fail" : "pass"} label={check.problems ? `${check.problems} row(s) need fixing` : "All rows OK"} testId="employer-import-status" />
            <span style={{ color: "var(--muted)" }}>{check.users.length} user row(s), {check.employers.length} employer row(s) in {check.filename}</span>
            {check.invites_queued && <span className="text-xs" style={{ color: "var(--muted)" }}>Invitations will be queued until go-live.</span>}
          </div>
          <RowTable title="Employers" rows={check.employers} columns={["name", "company_id"]} testId="employer-import-employers" />
          <RowTable title="Employer users" rows={check.users} columns={["name", "work_email", "company"]} testId="employer-import-users" />
          <button type="button" className="btn-primary mt-4" disabled={Boolean(check.problems) || busy} onClick={commit} data-testid="employer-import-commit-btn">
            {busy ? "Importing…" : "Import and invite"}
          </button>
        </div>
      )}
    </LaunchCard>
  );
}
